import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useElementBox, type ElementComponent } from './elementRenderContext';
import { useListing } from './ListingContext';
import { goToListing, listingHref, type ListingAddress } from './listingAddress';
import { useListingWords } from './listingWords';
import './elementListingPagination.css';

// Sleek's page numbers under a listing: the first, the last, and two either side of the current page, with
// the gaps between them shown as an ellipsis. Each is a real link to its own address, so a page can be
// opened in a new tab or reached by a search engine; a plain click moves in place and back to the top.

/** How many page numbers stand either side of the current one. */
const AROUND = 2;

// Lists the page numbers a pager shows, with null standing for a gap.
export function pageNumbers(current: number, last: number): (number | null)[] {
  const shown = new Set([1, last]);
  for (let at = current - AROUND; at <= current + AROUND; at += 1) if (at >= 1 && at <= last) shown.add(at);
  const sorted = [...shown].sort((a, b) => a - b);
  return sorted.flatMap((page, index) => (index > 0 && page - sorted[index - 1] > 1 ? [null, page] : [page]));
}

// Moves to another page in place and back to the top of the page, unless the shopper asked for a new tab.
function goToPage(event: React.MouseEvent<HTMLAnchorElement>, address: ListingAddress, page: number): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  goToListing({ ...address, page });
  document.getElementById('main-content')?.scrollIntoView({ block: 'start' });
}

/** One link of the pager: the page it goes to, and what it says. */
type PageLinkProps = { address: ListingAddress; page: number; label: string; children: React.ReactNode; className: string };

// Draws one link of the pager.
const PageLink: React.FC<PageLinkProps> = ({ address, page, label, children, className }) => (
  <a className={className} href={listingHref({ ...address, page })} aria-label={label} onClick={(event) => goToPage(event, address, page)}>{children}</a>
);

// Draws the pager of the page's listing, or nothing while it fits on one page.
export const ListingPaginationElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const listing = useListing();
  const words = useListingWords();
  const data = listing?.read.data;
  if (!listing || !data || data.pages <= 1) return null;
  const { address } = listing;
  return (
    <nav {...box} className={[box.className, 'xe-pages'].filter(Boolean).join(' ')} aria-label={words.pages}>
      {data.page > 1 ? <PageLink className="xe-page xe-page-step" address={address} page={data.page - 1} label={words.previous}><ChevronLeft size={18} aria-hidden="true" /></PageLink> : null}
      {pageNumbers(data.page, data.pages).map((page, index) => {
        if (page === null) return <span key={`gap-${index}`} className="xe-page-gap" aria-hidden="true">…</span>;
        if (page === data.page) return <span key={page} className="xe-page" aria-current="page">{page}</span>;
        return <PageLink key={page} className="xe-page" address={address} page={page} label={words.page(page)}>{page}</PageLink>;
      })}
      {data.page < data.pages ? <PageLink className="xe-page xe-page-step" address={address} page={data.page + 1} label={words.next}><ChevronRight size={18} aria-hidden="true" /></PageLink> : null}
    </nav>
  );
};
